import { useState, useRef, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2, LifeBuoy, ChevronDown, ChevronUp, ChevronRight,
  Send, User, Shield, Inbox, PlusCircle, Mail, MailOpen,
  Archive, ArchiveRestore, Trash2, AlertTriangle, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/language";

const CATEGORIES = [
  "Bug Report",
  "Question about Study Content",
  "Exam / Session Issue",
  "Account & Access",
  "Feature Request",
  "Other",
];

const CATEGORY_HINTS: Record<string, string> = {
  "Bug Report": "Something isn't working as expected — describe what happened and what you expected.",
  "Question about Study Content": "A question you think is incorrect, missing, or needs clarification.",
  "Exam / Session Issue": "Problem during a practice session or the CPL Written Exam.",
  "Account & Access": "Login issues, profile problems, or access requests.",
  "Feature Request": "An idea for something new or an improvement.",
  "Other": "Anything that doesn't fit above.",
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  open:        { label: "OPEN",        color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  in_progress: { label: "IN PROGRESS", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  resolved:    { label: "RESOLVED",    color: "bg-primary/20 text-primary border-primary/30" },
};

interface SupportTicket {
  id: number;
  category: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
  archivedAt: string | null;
}

interface TicketMessage {
  id: number;
  ticketId: number;
  senderRole: "admin" | "user";
  senderName: string;
  message: string;
  createdAt: string;
  readAt: string | null;
}

interface AdminMessage {
  id: number;
  subject: string;
  body: string;
  sentAt: string;
  readAt: string | null;
}

async function fetchWithAuth(path: string, options?: RequestInit) {
  const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Ticket conversation thread ────────────────────────────────────────────────
function TicketConversation({ ticket }: { ticket: SupportTicket }) {
  const qc = useQueryClient();
  const { t } = useLanguage();
  const c = t.contact;
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: messages = [], isLoading } = useQuery<TicketMessage[]>({
    queryKey: ["my-ticket-messages", ticket.id],
    queryFn: () => fetchWithAuth(`/api/support/tickets/${ticket.id}/messages`),
    refetchInterval: 20000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // When messages are loaded, the server marks admin replies as read — refresh the TowerLight count
  const hasUnreadAdmin = messages.some(m => m.senderRole === "admin" && !m.readAt);
  useEffect(() => {
    if (!isLoading) {
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
    }
  }, [isLoading, hasUnreadAdmin, qc]);

  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await fetchWithAuth(`/api/support/tickets/${ticket.id}/messages`, {
        method: "POST",
        body: JSON.stringify({ message: reply.trim() }),
      });
      setReply("");
      qc.invalidateQueries({ queryKey: ["my-ticket-messages", ticket.id] });
      qc.invalidateQueries({ queryKey: ["my-tickets"] });
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) sendReply();
  };

  return (
    <div className="border-t border-border/40 bg-secondary/10 p-4 space-y-4">
      <div>
        <p className="text-xs font-mono text-muted-foreground/50 uppercase tracking-wider mb-2">{c.threadOriginalMsg}</p>
        <div className="bg-card border border-border rounded-md p-3">
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{ticket.message}</p>
          <p className="text-xs text-muted-foreground/40 font-mono mt-2">
            {new Date(ticket.createdAt).toLocaleString()}
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground font-mono animate-pulse">{c.threadLoading}</p>
      ) : messages.length === 0 ? (
        <p className="text-xs text-muted-foreground/50 italic">{c.threadNoReplies}</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {messages.map(msg => {
            const isAdmin = msg.senderRole === "admin";
            return (
              <div key={msg.id} className={cn("flex gap-2.5 items-start", isAdmin ? "flex-row" : "flex-row-reverse")}>
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5",
                  isAdmin ? "bg-primary/20" : "bg-secondary")}>
                  {isAdmin
                    ? <Shield className="w-3.5 h-3.5 text-primary" />
                    : <User className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <div className={cn("max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed",
                  isAdmin
                    ? "bg-primary/10 border border-primary/20 text-foreground rounded-tl-none"
                    : "bg-card border border-border text-foreground rounded-tr-none")}>
                  <p className="whitespace-pre-wrap">{msg.message}</p>
                  <p className={cn("text-xs mt-1 font-mono",
                    isAdmin ? "text-primary/50" : "text-muted-foreground/50 text-right")}>
                    {msg.senderName} · {new Date(msg.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      )}

      {ticket.status !== "resolved" ? (
        <div className="space-y-2 pt-1">
          <Textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={c.threadReplyHint}
            className="text-sm min-h-[80px] resize-none bg-background"
          />
          <div className="flex justify-end">
            <Button size="sm" disabled={!reply.trim() || sending} onClick={sendReply} className="font-mono text-xs gap-2">
              <Send className="w-3.5 h-3.5" />
              {sending ? c.threadSending : c.threadSend}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground/40 italic font-mono">
          {c.threadResolved}
        </p>
      )}
    </div>
  );
}

// ── My Tickets tab ─────────────────────────────────────────────────────────────
function MyTickets() {
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const { data: tickets = [], isLoading } = useQuery<SupportTicket[]>({
    queryKey: ["my-tickets", showArchived],
    queryFn: () => fetchWithAuth(`/api/support/tickets${showArchived ? "?archived=true" : ""}`),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) =>
      fetchWithAuth(`/api/support/tickets/${id}/archive`, { method: "PATCH" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-tickets"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      fetchWithAuth(`/api/support/tickets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmDeleteId(null);
      setExpandedId(null);
      qc.invalidateQueries({ queryKey: ["my-tickets"] });
    },
  });

  if (isLoading) return <p className="text-primary font-mono text-sm animate-pulse tracking-widest">LOADING TICKETS...</p>;

  return (
    <div className="space-y-4">
      {/* Archived toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => { setShowArchived(v => !v); setExpandedId(null); }}
          className={cn(
            "flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded border transition-colors",
            showArchived
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"
          )}
        >
          {showArchived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
          {showArchived ? "Show Active" : "Show Archived"}
        </button>
      </div>

      {tickets.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Inbox className="w-12 h-12 text-muted-foreground/20 mx-auto" />
          <p className="text-muted-foreground font-mono text-sm">
            {showArchived ? "No archived tickets." : "No tickets yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map(ticket => {
            const isExpanded = expandedId === ticket.id;
            const cfg = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.open;
            const isConfirmingDelete = confirmDeleteId === ticket.id;
            return (
              <div key={ticket.id} className="border border-border rounded-md bg-card/50 overflow-hidden">
                <div className="flex items-stretch">
                  {/* Main expand button */}
                  <button
                    className="flex-1 text-left p-4 flex items-start gap-3 hover:bg-secondary/20 transition-colors min-w-0"
                    onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={cn("font-mono text-xs border", cfg.color)}>{cfg.label}</Badge>
                        <span className="text-xs text-muted-foreground font-mono ml-auto">
                          #{ticket.id} · {new Date(ticket.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="font-semibold text-foreground truncate">{ticket.subject}</div>
                      <div className="text-xs text-muted-foreground/60">{ticket.category}</div>
                    </div>
                    <div className="shrink-0 text-muted-foreground mt-1">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 pr-3 shrink-0 border-l border-border/40 pl-3">
                    <button
                      title={ticket.archivedAt ? "Unarchive" : "Archive"}
                      onClick={() => archiveMutation.mutate(ticket.id)}
                      disabled={archiveMutation.isPending}
                      className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors"
                    >
                      {ticket.archivedAt ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </button>
                    <button
                      title="Delete ticket"
                      onClick={() => setConfirmDeleteId(isConfirmingDelete ? null : ticket.id)}
                      className={cn(
                        "p-1.5 rounded transition-colors",
                        isConfirmingDelete
                          ? "text-red-400 bg-red-500/10"
                          : "text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                      )}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Delete confirmation */}
                {isConfirmingDelete && (
                  <div className="border-t border-red-500/20 bg-red-500/5 px-4 py-3 flex items-center gap-3">
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                    <p className="text-xs text-red-400 font-mono flex-1">
                      This will permanently delete the ticket and all messages. This cannot be undone.
                    </p>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs font-mono px-2.5 py-1 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(ticket.id)}
                        disabled={deleteMutation.isPending}
                        className="text-xs font-mono px-2.5 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                      >
                        {deleteMutation.isPending ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </div>
                )}

                {isExpanded && <TicketConversation ticket={ticket} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── New Ticket form ────────────────────────────────────────────────────────────
function NewTicketForm({ onSubmitted }: { onSubmitted: () => void }) {
  const qc = useQueryClient();
  const { t } = useLanguage();
  const c = t.contact;
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const canSubmit = category && subject.trim().length >= 3 && message.trim().length >= 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await fetchWithAuth("/api/support/tickets", {
        method: "POST",
        body: JSON.stringify({ category, subject: subject.trim(), message: message.trim() }),
      });
      qc.invalidateQueries({ queryKey: ["my-tickets"] });
      setDone(true);
    } catch (err: unknown) {
      setError((err as Error).message || c.failedSubmit);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-5 animate-in fade-in duration-500">
        <div className="bg-primary/10 rounded-full p-5">
          <CheckCircle2 className="w-10 h-10 text-primary" />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-xl font-bold font-mono tracking-wide text-foreground">{c.successTitle}</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">{c.successBody}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setDone(false); setCategory(""); setSubject(""); setMessage(""); }}>
            {c.submitAnother}
          </Button>
          <Button onClick={onSubmitted}>{c.viewMyTickets}</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{c.categoryLabel} <span className="text-destructive">*</span></label>
        <div className="relative">
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className={cn("w-full h-11 rounded-md border bg-background px-3 pr-9 text-sm appearance-none transition-colors",
              category ? "border-primary/50 text-foreground" : "border-border text-muted-foreground")}
          >
            <option value="">{c.categoryPlaceholder}</option>
            {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
        {category && (
          <p className="text-xs text-muted-foreground italic pl-1 animate-in fade-in duration-200">
            {CATEGORY_HINTS[category]}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{c.subjectLabel} <span className="text-destructive">*</span></label>
        <Input value={subject} onChange={e => setSubject(e.target.value)}
          placeholder={c.subjectPlaceholder} maxLength={120} className="bg-background" />
        <p className="text-xs text-muted-foreground/60 text-right">{subject.length}/120</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{c.messageLabel} <span className="text-destructive">*</span></label>
        <Textarea value={message} onChange={e => setMessage(e.target.value)}
          placeholder={c.messagePlaceholder}
          className="min-h-[160px] bg-background resize-none" maxLength={2000} />
        <p className="text-xs text-muted-foreground/60 text-right">{message.length}/2000</p>
      </div>

      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-destructive text-sm">{error}</CardContent>
        </Card>
      )}

      <div className="flex items-center gap-4">
        <Button type="submit" size="lg" disabled={!canSubmit || submitting} className="font-mono tracking-wider">
          {submitting ? c.submitting : c.submitBtn}
        </Button>
        {!canSubmit && (
          <p className="text-xs text-muted-foreground">
            {!category ? c.validationCategory : subject.trim().length < 3 ? c.validationSubject : c.validationMessage}
          </p>
        )}
      </div>
    </form>
  );
}

// ── Admin Messages (Inbox) tab ────────────────────────────────────────────────
function AdminInbox() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const { data: messages = [], isLoading } = useQuery<AdminMessage[]>({
    queryKey: ["user-messages"],
    queryFn: () => fetchWithAuth("/api/user/messages"),
  });

  const markRead = useMutation({
    mutationFn: (id: number) =>
      fetchWithAuth(`/api/user/messages/${id}/read`, { method: "PATCH" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-messages"] });
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      fetchWithAuth(`/api/user/messages/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmDeleteId(null);
      setOpenId(null);
      qc.invalidateQueries({ queryKey: ["user-messages"] });
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
    },
  });

  function handleOpen(msg: AdminMessage) {
    setOpenId(openId === msg.id ? null : msg.id);
    if (!msg.readAt) markRead.mutate(msg.id);
  }

  if (isLoading) {
    return <div className="text-primary font-mono text-sm animate-pulse tracking-widest">LOADING MESSAGES...</div>;
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <Mail className="w-12 h-12 text-muted-foreground/20 mx-auto" />
        <p className="text-muted-foreground font-mono text-sm">No messages yet.</p>
        <p className="text-muted-foreground/40 font-mono text-xs">Messages from the admin team will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {messages.map(msg => {
        const isOpen = openId === msg.id;
        const isUnread = !msg.readAt;
        const isConfirmingDelete = confirmDeleteId === msg.id;
        return (
          <div key={msg.id} className={cn(
            "border rounded-md overflow-hidden transition-all",
            isUnread ? "border-primary/40 bg-primary/5" : "border-border bg-card/50"
          )}>
            <div className="flex items-stretch">
              <button
                onClick={() => handleOpen(msg)}
                className="flex-1 flex items-center gap-3 px-4 py-3.5 text-left hover:bg-secondary/20 transition-colors min-w-0"
              >
                <div className={cn("shrink-0", isUnread ? "text-primary" : "text-muted-foreground/40")}>
                  {isUnread ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn("font-medium text-sm truncate",
                      isUnread ? "text-foreground font-semibold" : "text-foreground/80")}>
                      {msg.subject}
                    </span>
                    {isUnread && (
                      <span className="shrink-0 text-[9px] font-mono font-bold bg-primary/20 text-primary border border-primary/30 rounded px-1.5 py-0.5 uppercase tracking-wider">
                        New
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground/60 font-mono mt-0.5">
                    {new Date(msg.sentAt).toLocaleDateString("en-CA", {
                      year: "numeric", month: "short", day: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                </div>
                <div className={cn("shrink-0 text-xs font-mono text-muted-foreground/40 transition-transform",
                  isOpen ? "rotate-180" : "")}>
                  ▼
                </div>
              </button>

              {/* Delete button */}
              <div className="flex items-center px-3 shrink-0 border-l border-border/40">
                <button
                  title="Delete message"
                  onClick={() => setConfirmDeleteId(isConfirmingDelete ? null : msg.id)}
                  className={cn(
                    "p-1.5 rounded transition-colors",
                    isConfirmingDelete
                      ? "text-red-400 bg-red-500/10"
                      : "text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                  )}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Delete confirmation */}
            {isConfirmingDelete && (
              <div className="border-t border-red-500/20 bg-red-500/5 px-4 py-3 flex items-center gap-3">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                <p className="text-xs text-red-400 font-mono flex-1">
                  Permanently delete this message? This cannot be undone.
                </p>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="text-xs font-mono px-2.5 py-1 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(msg.id)}
                    disabled={deleteMutation.isPending}
                    className="text-xs font-mono px-2.5 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            )}

            {isOpen && (
              <div className="px-4 pb-5 pt-1 border-t border-border/40">
                <div className="text-sm text-foreground/90 font-mono leading-relaxed whitespace-pre-wrap bg-secondary/20 rounded-md p-4">
                  {msg.body}
                </div>
                <div className="text-[10px] text-muted-foreground/40 font-mono mt-2">
                  From: Flight Deck Admin Team
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Newsletter Compose (admin / mod only) ─────────────────────────────────────
interface Newsletter { id: number; subject: string; body: string; sentAt: string | null; sentByName: string | null; recipientCount: number; }

function NewsletterCompose() {
  const qc = useQueryClient();
  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data: newsletters = [] } = useQuery<Newsletter[]>({
    queryKey: ["newsletters"],
    queryFn: () => fetchWithAuth("/api/newsletters"),
  });

  const sendMutation = useMutation({
    mutationFn: () => fetchWithAuth("/api/newsletters", {
      method: "POST",
      body: JSON.stringify({ subject: subject.trim(), body: body.trim() }),
    }),
    onSuccess: (_data: unknown) => {
      setSubject("");
      setBody("");
      qc.invalidateQueries({ queryKey: ["newsletters"] });
    },
  });

  return (
    <div className="space-y-6">
      <Card className="border-border bg-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            <span className="text-sm font-bold font-mono tracking-wide">COMPOSE NEWSLETTER</span>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Subject</Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="e.g. New CPL questions added"
              className="font-mono text-sm bg-background"
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Body</Label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Write the newsletter content here..."
              rows={7}
              maxLength={8000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
            <div className="text-[10px] text-muted-foreground/50 font-mono text-right">{body.length}/8000</div>
          </div>

          <Button
            disabled={!subject.trim() || !body.trim() || sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
            className="font-mono text-xs uppercase tracking-widest"
          >
            {sendMutation.isPending
              ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Sending…</>
              : <><Send className="h-3.5 w-3.5 mr-2" />Send to Subscribers</>}
          </Button>

          {sendMutation.isSuccess && (
            <p className="text-xs text-primary font-mono animate-in fade-in duration-300">
              ✓ Newsletter sent to {(sendMutation.data as { recipientCount?: number })?.recipientCount ?? 0} subscribers.
            </p>
          )}
          {sendMutation.isError && (
            <p className="text-xs text-destructive font-mono">{sendMutation.error instanceof Error ? sendMutation.error.message : undefined}</p>
          )}
        </CardContent>
      </Card>

      {newsletters.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60 px-1">Sent newsletters</div>
          {newsletters.map(nl => (
            <div key={nl.id} className="border border-border/60 rounded-md bg-card/50 overflow-hidden">
              <button
                className="w-full text-left px-4 py-3 flex items-start gap-2 hover:bg-secondary/20 transition-colors"
                onClick={() => setExpanded(expanded === nl.id ? null : nl.id)}
              >
                <Mail className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm font-semibold truncate">{nl.subject}</div>
                  <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                    {nl.sentAt ? new Date(nl.sentAt).toLocaleString("en-CA") : "Draft"} · {nl.recipientCount} recipients
                    {nl.sentByName && <span className="ml-1">· by {nl.sentByName}</span>}
                  </div>
                </div>
                {expanded === nl.id
                  ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />}
              </button>
              {expanded === nl.id && (
                <div className="px-4 pb-4 pt-0 border-t border-border/40">
                  <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap leading-relaxed mt-3">{nl.body}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function Contact() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const c = t.contact;
  const isPrivileged = ["admin", "master_admin", "moderator"].includes(user?.role ?? "");
  const [tab, setTab] = useState<"new" | "mine" | "inbox" | "newsletter">("new");
  const qc = useQueryClient();

  // Mark everything as read the moment the user lands on the Tower page
  useEffect(() => {
    fetchWithAuth("/api/user/messages/mark-all-read", { method: "PATCH" }).then(() => {
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
      qc.invalidateQueries({ queryKey: ["user-messages"] });
      qc.invalidateQueries({ queryKey: ["my-tickets"] });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: messages = [] } = useQuery<AdminMessage[]>({
    queryKey: ["user-messages"],
    queryFn: () => fetchWithAuth("/api/user/messages"),
    staleTime: 30_000,
  });
  const unreadCount = messages.filter(m => !m.readAt).length;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <LifeBuoy className="w-6 h-6 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{c.title}</h1>
          </div>
          <p className="text-muted-foreground">{c.subtitle}</p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-0 border-b border-border">
          <button
            onClick={() => setTab("new")}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 text-sm font-mono font-medium border-b-2 -mb-px transition-colors",
              tab === "new" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <PlusCircle className="w-4 h-4" />
            {c.tabNew}
          </button>

          <button
            onClick={() => setTab("mine")}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 text-sm font-mono font-medium border-b-2 -mb-px transition-colors",
              tab === "mine" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <Inbox className="w-4 h-4" />
            {c.tabMine}
          </button>

          <button
            onClick={() => setTab("inbox")}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 text-sm font-mono font-medium border-b-2 -mb-px transition-colors",
              tab === "inbox" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <Mail className="w-4 h-4" />
            {c.tabInbox}
            {unreadCount > 0 && (
              <span className="text-[9px] font-mono font-bold bg-primary/20 text-primary border border-primary/30 rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {isPrivileged && (
            <button
              onClick={() => setTab("newsletter")}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-mono font-medium border-b-2 -mb-px transition-colors",
                tab === "newsletter" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Send className="w-4 h-4" />
              {c.tabNewsletter}
            </button>
          )}
        </div>

        {/* Tab content */}
        {tab === "new"        && <NewTicketForm onSubmitted={() => setTab("mine")} />}
        {tab === "mine"       && <MyTickets />}
        {tab === "inbox"      && <AdminInbox />}
        {tab === "newsletter" && isPrivileged && <NewsletterCompose />}
      </div>
    </Layout>
  );
}
